// frontend/src/utils/execution/executionRules.js

export const EXECUTION_DECISIONS = {
  ALLOW: 'ALLOW',
  BLOCK: 'BLOCK',
  CONFIRM: 'CONFIRM',
};

function normalizeName(value) {
  return String(value || '').trim().toLowerCase();
}

function getRelationshipName(relationship = {}) {
  return (
    relationship.name ||
    relationship.first_name ||
    relationship.personName ||
    null
  );
}

function normalizeTransition(intent = {}, targetEntity = null) {
  const rawType = intent?.type || intent?.intent || null;

  if (!rawType) {
    return null;
  }

  if (rawType.startsWith('relationship_')) {
    return {
      category: 'relationship',
      operation: rawType.replace('relationship_', ''),
      target: targetEntity,
    };
  }

  if (rawType === 'memory_write') {
    return {
      category: 'memory',
      operation: 'write',
      target: null,
    };
  }

  return {
    category: 'unknown',
    operation: rawType,
    target: targetEntity,
  };
}

function buildResult(transition, decision, reason) {
  return {
    decision,
    reason,
    category: transition?.category || null,
    operation: transition?.operation || null,
  };
}

function relationshipExists(target, relationshipState = []) {
  const targetName = normalizeName(target?.name);

  if (!targetName) return false;

  return relationshipState.some((relationship) => {
    const relationshipName = normalizeName(getRelationshipName(relationship));
    return relationshipName === targetName;
  });
}

function evaluateRelationshipRules(transition, context) {
  if (transition.category !== 'relationship') return null;

  const { operation, target } = transition;
  const { relationshipState } = context;

  if (!operation) {
    return buildResult(
      transition,
      EXECUTION_DECISIONS.BLOCK,
      'Relationship transition missing operation'
    );
  }

  if (operation === 'add') {
    if (!target?.name) {
      return buildResult(
        transition,
        EXECUTION_DECISIONS.BLOCK,
        'Relationship add blocked because target is missing'
      );
    }

    return buildResult(
      transition,
      EXECUTION_DECISIONS.ALLOW,
      `Relationship add allowed for ${target.name}`
    );
  }

  if (operation === 'remove') {
    if (!target?.name) {
      return buildResult(
        transition,
        EXECUTION_DECISIONS.BLOCK,
        'Relationship removal blocked because target is missing'
      );
    }

    if (!relationshipExists(target, relationshipState)) {
      return buildResult(
        transition,
        EXECUTION_DECISIONS.BLOCK,
        `Relationship removal blocked because ${target.name} was not found in active relationship state`
      );
    }

    return buildResult(
      transition,
      EXECUTION_DECISIONS.ALLOW,
      `Relationship removal allowed for ${target.name}`
    );
  }

  if (operation === 'update') {
    if (!target?.name) {
      return buildResult(
        transition,
        EXECUTION_DECISIONS.BLOCK,
        'Relationship update blocked because target is missing'
      );
    }

    return buildResult(
      transition,
      EXECUTION_DECISIONS.ALLOW,
      `Relationship update allowed for ${target.name}`
    );
  }

  return buildResult(
    transition,
    EXECUTION_DECISIONS.ALLOW,
    `Relationship ${operation} allowed by default relationship rule`
  );
}

function evaluateMemoryRules(transition, context) {
  if (transition.category !== 'memory') return null;

  const { message } = context;
  const cleanMessage = String(message || '').trim();

  if (transition.operation !== 'write') {
    return buildResult(
      transition,
      EXECUTION_DECISIONS.ALLOW,
      `Memory ${transition.operation} allowed by default memory rule`
    );
  }

  if (cleanMessage.length < 3) {
    return buildResult(
      transition,
      EXECUTION_DECISIONS.BLOCK,
      'Memory write blocked because message is too short'
    );
  }

  return buildResult(
    transition,
    EXECUTION_DECISIONS.ALLOW,
    'Memory write allowed'
  );
}

export function evaluateExecutionRules(context = {}) {
  const { intent, targetEntity } = context;

  const transition = normalizeTransition(intent, targetEntity);

  if (!transition) {
    return {
      decision: EXECUTION_DECISIONS.ALLOW,
      reason: 'No transition detected',
      category: null,
      operation: null,
    };
  }

  const ruleGroups = [
    evaluateRelationshipRules,
    evaluateMemoryRules,
  ];

  for (const ruleFn of ruleGroups) {
    const result = ruleFn(transition, context);
    if (result) return result;
  }

  return buildResult(
    transition,
    EXECUTION_DECISIONS.ALLOW,
    'No blocking rules triggered'
  );
}